import authService from './authService';
import {
  ProductAggregationMaster,
  ProductAggregationMasterFormData,
  ProductAggregationDetail,
  ProductAggregationDetailFormData,
  ProductAggregationDetailUpdateData,
  CompositeProduct,
  CompositeProductSearchModel,
} from '../types/compositeProduct';
import { ApiResponse, PaginatedResponse } from '../types/common';

// ─── Service ──────────────────────────────────────────────────────────────────

class CompositeProductService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ── Masters ───────────────────────────────────────────────────────────────

  /**
   * Listagem paginada de produtos compostos com filtros de busca.
   * GET /api/product-aggregation
   */
  async getCompositeProducts(
    model: CompositeProductSearchModel,
  ): Promise<PaginatedResponse<ProductAggregationMaster>> {
    const params = new URLSearchParams({
      page: model.page.toString(),
      limit: model.pageSize.toString(),
      ...(model.searchSkuMaster && { skuMaster: model.searchSkuMaster }),
      ...(model.searchSkuDetail && { skuDetail: model.searchSkuDetail }),
      ...(model.searchDescription && { description: model.searchDescription }),
      ...(model.orderBy && { orderBy: model.orderBy }),
      ...(model.orderDirection && { orderDirection: model.orderDirection }),
    });

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/product-aggregation?${params}`);

    if (!response.ok) throw new Error('Falha ao listar produtos compostos');
    return response.json();
  }

  /**
   * Retorna um produto composto (master) pelo SkuMaster, incluindo a lista de detalhes.
   * GET /api/product-aggregation/:skuMaster
   */
  async getCompositeProductBySkuMaster(skuMaster: string): Promise<CompositeProduct> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/product-aggregation/${encodeURIComponent(skuMaster)}`);

    if (!response.ok) {
      if (response.status === 404) throw new Error('Produto composto não encontrado');
      throw new Error('Falha ao carregar produto composto');
    }

    const data: ApiResponse<CompositeProduct> = await response.json();
    return data.data!;
  }

  /**
   * Cria um novo produto composto (apenas o master; os detalhes são adicionados separadamente).
   * POST /api/product-aggregation
   * Body: { skuMaster, description }
   */
  async createCompositeProduct(
    formData: ProductAggregationMasterFormData,
  ): Promise<ProductAggregationMaster> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/product-aggregation', formData);

    if (!response.ok) {
      if (response.status === 409) throw new Error(`SKU Master '${formData.skuMaster}' já está em uso`);
      throw new Error('Falha ao criar produto composto');
    }

    const data: ApiResponse<ProductAggregationMaster> = await response.json();
    return data.data!;
  }

  /**
   * Atualiza a descrição de um produto composto.
   * PUT /api/product-aggregation/:skuMaster
   * Body: { description }
   */
  async updateCompositeProduct(
    skuMaster: string,
    description: string,
  ): Promise<ProductAggregationMaster> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(
      `/product-aggregation/${encodeURIComponent(skuMaster)}`,
      { description },
    );

    if (!response.ok) {
      if (response.status === 404) throw new Error('Produto composto não encontrado');
      throw new Error('Falha ao atualizar produto composto');
    }

    const data: ApiResponse<ProductAggregationMaster> = await response.json();
    return data.data!;
  }

  /**
   * Remove um produto composto e todos os seus detalhes.
   * DELETE /api/product-aggregation/:skuMaster
   */
  async deleteCompositeProduct(skuMaster: string): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/product-aggregation/${encodeURIComponent(skuMaster)}`);

    if (!response.ok) {
      if (response.status === 404) throw new Error('Produto composto não encontrado');
      throw new Error('Falha ao excluir produto composto');
    }
  }

  // ── Details ───────────────────────────────────────────────────────────────

  /**
   * Retorna os detalhes de um produto composto.
   * GET /api/product-aggregation/:skuMaster/details
   */
  async getCompositeDetails(skuMaster: string): Promise<ProductAggregationDetail[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/product-aggregation/${encodeURIComponent(skuMaster)}/details`,
    );

    if (!response.ok) throw new Error('Falha ao carregar detalhes do produto composto');

    const data: ApiResponse<ProductAggregationDetail[]> = await response.json();
    return data.data!;
  }

  /**
   * Adiciona um produto filho a um produto composto.
   * POST /api/product-aggregation/:skuMaster/details
   * Body: ProductAggregationDetailFormData
   */
  async addCompositeDetail(
    skuMaster: string,
    formData: ProductAggregationDetailFormData,
  ): Promise<ProductAggregationDetail> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(
      `/product-aggregation/${encodeURIComponent(skuMaster)}/details`,
      formData,
    );

    if (!response.ok) {
      if (response.status === 409) throw new Error(`SKU '${formData.skuDetail}' já faz parte deste produto composto`);
      if (response.status === 404) throw new Error('Produto filho não encontrado no cadastro de produtos');
      throw new Error('Falha ao adicionar produto filho');
    }

    const data: ApiResponse<ProductAggregationDetail> = await response.json();
    return data.data!;
  }

  /**
   * Atualiza os campos editáveis de um detalhe (qty, isGift, promoPrice, usePromoPrice).
   * PUT /api/product-aggregation/:skuMaster/details/:id
   * Body: ProductAggregationDetailUpdateData
   */
  async updateCompositeDetail(
    skuMaster: string,
    detailId: number,
    updateData: ProductAggregationDetailUpdateData,
  ): Promise<ProductAggregationDetail> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(
      `/product-aggregation/${encodeURIComponent(skuMaster)}/details/${detailId}`,
      updateData,
    );

    if (!response.ok) {
      if (response.status === 404) throw new Error('Detalhe não encontrado');
      throw new Error('Falha ao atualizar detalhe do produto composto');
    }

    const data: ApiResponse<ProductAggregationDetail> = await response.json();
    return data.data!;
  }

  /**
   * Remove um produto filho de um produto composto.
   * DELETE /api/product-aggregation/:skuMaster/details/:id
   */
  async removeCompositeDetail(skuMaster: string, detailId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(
      `/product-aggregation/${encodeURIComponent(skuMaster)}/details/${detailId}`,
    );

    if (!response.ok) {
      if (response.status === 404) throw new Error('Detalhe não encontrado');
      throw new Error('Falha ao remover produto filho');
    }
  }

  /**
   * Ativa ou desativa um produto composto.
   * PATCH /api/product-aggregation/:skuMaster/active
   * Body: { isActive: boolean }
   */
  async toggleCompositeProductActive(
    skuMaster: string,
    isActive: boolean,
  ): Promise<ProductAggregationMaster> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(
      `/product-aggregation/${encodeURIComponent(skuMaster)}/active`,
      { isActive },
    );

    if (!response.ok) {
      if (response.status === 404) throw new Error('Produto composto não encontrado');
      if (response.status === 403) throw new Error('Sem permissão para alterar o status');
      throw new Error('Falha ao atualizar status do produto composto');
    }

    const data: ApiResponse<ProductAggregationMaster> = await response.json();
    return data.data!;
  }

  /**
   * Altera a visibilidade no CRM de um produto composto.
   * PATCH /api/product-aggregation/:skuMaster/visible-on-crm
   * Body: { isVisibleOnCRM: boolean }
   */
  async toggleCompositeProductVisibleOnCRM(
    skuMaster: string,
    isVisibleOnCRM: boolean,
  ): Promise<ProductAggregationMaster> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(
      `/product-aggregation/${encodeURIComponent(skuMaster)}/visible-on-crm`,
      { isVisibleOnCRM },
    );

    if (!response.ok) {
      if (response.status === 404) throw new Error('Produto composto não encontrado');
      if (response.status === 403) throw new Error('Sem permissão para alterar a visibilidade');
      throw new Error('Falha ao atualizar visibilidade no CRM');
    }

    const data: ApiResponse<ProductAggregationMaster> = await response.json();
    return data.data!;
  }
}

export const compositeProductService = new CompositeProductService();
export default compositeProductService;
